var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/* eslint-disable no-console */
import React, { useContext, useState, useEffect, useMemo, useCallback, useRef, } from 'react';
import { initialize, } from 'launchdarkly-js-client-sdk';
import { MultivariateEnrolment, } from './types';
export * from './types';
export var FeatureFlagContext = React.createContext({
    getBoolean: function (_flagKey, options) {
        if (options === void 0) { options = { defaultValue: false }; }
        return options.defaultValue;
    },
    getMultivariate: function (_flagKey, options) {
        if (options === void 0) { options = {
            defaultValue: MultivariateEnrolment.NOT_ENROLLED,
        }; }
        return options.defaultValue;
    },
    getNumber: function (_flagKey, options) {
        if (options === void 0) { options = { defaultValue: undefined }; }
        return options.defaultValue;
    },
    getJson: function (_flagKey, options) {
        if (options === void 0) { options = { defaultValue: undefined }; }
        return options.defaultValue;
    },
    ldClient: undefined,
});
export var useFeatureFlagClient = function () { return useContext(FeatureFlagContext); };
export var FeatureFlagProvider = function (_a) {
    var children = _a.children, userId = _a.userId, _b = _a.additionalAttributes, additionalAttributes = _b === void 0 ? {} : _b, track = _a.track, showInitialisationFailed = _a.showInitialisationFailed, launchDarklyClientID = _a.launchDarklyClientID;
    var _c = useState(true), loading = _c[0], setLoading = _c[1];
    var userContext = useMemo(function () { return (__assign({ kind: 'user', key: userId, anonymous: !userId }, additionalAttributes)); }, [userId, additionalAttributes]);
    var ldClient = useState(function () {
        return initialize(launchDarklyClientID, userContext);
    })[0];
    var onReady = useCallback(function () {
        setLoading(false);
    }, []);
    var onFailed = useCallback(function () {
        console.error("LaunchDarkly client failed to initialise, all flags will be evaluated with their default value");
        showInitialisationFailed('Failed to load page configuration, please refresh.');
    }, [showInitialisationFailed]);
    useEffect(function () {
        ldClient.on('ready', onReady);
        ldClient.on('failed', onFailed);
        return function () {
            ldClient.off('ready', onReady);
            ldClient.off('failed', onFailed);
        };
    }, [ldClient, onFailed, onReady]);
    useEffect(function () {
        void ldClient.identify(userContext);
    }, [ldClient, userContext]);
    var flagExposures = useRef({});
    var trackFlagExposureEvent = useCallback(function (flagKey, flagValue, alwaysEmit) {
        if (alwaysEmit === void 0) { alwaysEmit = false; }
        // debounce duplicate flag exposure events
        if (!alwaysEmit && Object.keys(flagExposures.current).includes(flagKey)) {
            return;
        }
        if (userId) {
            track(flagKey, flagValue);
            flagExposures.current[flagKey] = flagValue;
        }
    }, [track, userId]);
    var getFlag = useCallback(function (flagKey, options) {
        var flagValue = ldClient.variation(flagKey, options.defaultValue);
        if (!options.disableExposureTracking) {
            trackFlagExposureEvent(flagKey, flagValue, false);
        }
        return flagValue;
    }, [ldClient, trackFlagExposureEvent]);
    var getBoolean = useCallback(function (flagKey, options) {
        if (options === void 0) { options = { defaultValue: false }; }
        return getFlag(flagKey, options);
    }, [getFlag]);
    var getMultivariate = useCallback(function (flagKey, options) {
        if (options === void 0) { options = {
            defaultValue: MultivariateEnrolment.NOT_ENROLLED,
        }; }
        var flagValue = getFlag(flagKey, options);
        if (!Object.values(MultivariateEnrolment).includes(flagValue)) {
            console.warn("unexpected multivariate flag value: \"".concat(flagValue, "\" for flag \"").concat(flagKey, "\""));
            return options.defaultValue;
        }
        return flagValue;
    }, [getFlag]);
    var getNumber = useCallback(function (flagKey, options) {
        if (options === void 0) { options = { defaultValue: undefined }; }
        return getFlag(flagKey, options);
    }, [getFlag]);
    var getJson = useCallback(function (flagKey, options) {
        if (options === void 0) { options = { defaultValue: undefined }; }
        return getFlag(flagKey, options);
    }, [getFlag]);
    return (React.createElement(FeatureFlagContext.Provider, { value: useMemo(function () { return ({
            getBoolean: getBoolean,
            getMultivariate: getMultivariate,
            getNumber: getNumber,
            getJson: getJson,
            ldClient: ldClient,
        }); }, [getBoolean, getMultivariate, getNumber, getJson, ldClient]) }, !loading && children));
};
export var useDynamicBooleanFlag = function (flagKey, options) {
    var _a = useFeatureFlagClient(), getBoolean = _a.getBoolean, ldClient = _a.ldClient;
    var _b = useState(function () {
        return getBoolean(flagKey, options);
    }), flagValue = _b[0], setFlagValue = _b[1];
    useEffect(function () {
        var setFlag = function (newFlagValue) {
            setFlagValue(newFlagValue);
        };
        ldClient === null || ldClient === void 0 ? void 0 : ldClient.on("change:".concat(flagKey), setFlag);
        return function () {
            ldClient === null || ldClient === void 0 ? void 0 : ldClient.off("change:".concat(flagKey), setFlag);
        };
    }, [flagKey, ldClient]);
    return flagValue;
};
